// pushService.ts
// Push notification permission and subscription logic using Klaviyo

import { NOTIFICATION, PERMISSION_STATUS } from "@/constants/notification";
import { detectPlatform } from "core/utils/utils.helper";
import { KlaviyoService } from "./klaviyo.service";
import { getKlaviyoVapidKey } from "@/utils/klaviyo.utils";

type BrowserInstructionConfig = {
  name: string;
  ios?: string[];
  android?: string[];
  web?: string[];
};

export type DetailedPermissionStatus =
  | typeof PERMISSION_STATUS.GRANTED
  | typeof PERMISSION_STATUS.DENIED
  | typeof PERMISSION_STATUS.DEFAULT
  | typeof PERMISSION_STATUS.BLOCKED
  | typeof PERMISSION_STATUS.UNSUPPORTED;

/**
 * Checks if the browser supports push notifications
 * Verifies Notification API, Service Worker, and Push Manager support
 */
export function isNotificationSupported(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * Gets detailed notification permission status
 * Distinguishes between default, denied, and blocked states
 */
export function getDetailedPermissionStatus(): DetailedPermissionStatus {
  if (!isNotificationSupported()) {
    return PERMISSION_STATUS.UNSUPPORTED;
  }

  const permission = Notification.permission;

  // Check if permission was permanently blocked
  // This happens when user denies multiple times or clicks "Block" in browser
  if (permission === PERMISSION_STATUS.DENIED) {
    // Try to detect if it's permanently blocked vs temporarily denied
    // Note: There's no reliable way to detect this in all browsers,
    // but we can check if the permission API is available
    if ("permissions" in navigator) {
      // Will return 'blocked' if we can't request again
      return PERMISSION_STATUS.BLOCKED;
    }
    return PERMISSION_STATUS.DENIED;
  }

  return permission as DetailedPermissionStatus;
}

/**
 * Gets current notification permission status
 */
export function getNotificationPermission(): NotificationPermission | null {
  if (!isNotificationSupported()) {
    return null;
  }
  return Notification.permission;
}

/**
 * Checks if notifications are blocked at the browser level
 * Returns true if user needs to manually enable in browser settings
 * Also detects incognito/private browsing mode where notifications are disabled
 */
export async function isNotificationBlocked(): Promise<boolean> {
  if (!isNotificationSupported()) {
    return true;
  }

  const currentPermission = Notification.permission;

  // If already denied, it's blocked
  if (currentPermission === "denied") {
    // Use Permissions API to check if it's permanently blocked
    if ("permissions" in navigator) {
      try {
        const result = await navigator.permissions.query({
          name: "notifications" as PermissionName,
        });
        // If state is 'denied', it's blocked
        return result.state === "denied";
      } catch (error) {
        // Permissions API not available or error - assume blocked if denied
        return true;
      }
    }
    // No Permissions API - if denied, likely blocked
    return true;
  }

  // If already granted, not blocked
  if (currentPermission === "granted") {
    return false;
  }

  // If permission is "default", we can't reliably detect incognito without requesting
  // Instead, we'll let the actual permission request handle it
  // This function returns false for "default" state, and the actual request will fail in incognito
  return false;
}

/**
 * Get instructions for enabling notifications based on platform and browser
 */
export function getEnableInstructions(): {
  title: string;
  steps: string[];
  browserName: string;
} {
  const userAgent = navigator.userAgent.toLowerCase();
  const platform = detectPlatform();

  const instructions = NOTIFICATION.ENABLE_INSTRUCTIONS;

  // Detect browser and get corresponding instructions
  let browserConfig: BrowserInstructionConfig = instructions.GENERIC;

  if (/chrome|crios|crmo/.test(userAgent) && !/edge|edg/.test(userAgent)) {
    browserConfig = instructions.CHROME;
  } else if (/safari/.test(userAgent) && !/chrome|crios|crmo/.test(userAgent)) {
    browserConfig = instructions.SAFARI;
  } else if (/firefox|fxios/.test(userAgent)) {
    browserConfig = instructions.FIREFOX;
  } else if (/edg|edge/.test(userAgent)) {
    browserConfig = instructions.EDGE;
  }

  // Get platform-specific steps
  const steps =
    browserConfig[platform] ||
    browserConfig.web ||
    Object.values(browserConfig).find((value) => Array.isArray(value)) ||
    [];

  return {
    title: `Enable Notifications in ${browserConfig.name}`,
    steps,
    browserName: browserConfig.name,
  };
}

/**
 * Clean up any old Firebase service workers that may still be registered.
 * This ensures a clean migration from Firebase to Klaviyo push notifications.
 */
export async function cleanupFirebaseServiceWorkers(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  try {
    // Unregister Firebase SW at root scope
    const rootRegistration = await navigator.serviceWorker.getRegistration("/");
    if (rootRegistration) {
      const scriptURL =
        rootRegistration.active?.scriptURL ||
        rootRegistration.installing?.scriptURL ||
        rootRegistration.waiting?.scriptURL;

      if (scriptURL?.includes("firebase-messaging-sw")) {
        console.log("[Push] Cleaning up old Firebase SW at root scope");
        await rootRegistration.unregister();
      }
    }

    // Unregister Firebase SW at /fcm/ scope
    const fcmRegistration =
      await navigator.serviceWorker.getRegistration("/fcm/");
    if (fcmRegistration) {
      const scriptURL =
        fcmRegistration.active?.scriptURL ||
        fcmRegistration.installing?.scriptURL ||
        fcmRegistration.waiting?.scriptURL;

      if (scriptURL?.includes("firebase-messaging-sw")) {
        console.log("[Push] Cleaning up old Firebase SW at /fcm/ scope");
        await fcmRegistration.unregister();
      }
    }
  } catch (error) {
    console.error("[Push] Error cleaning up Firebase service workers:", error);
  }
}

/**
 * Request push permission and subscribe to Klaviyo push notifications
 * @param accountId - User's account ID to link to Klaviyo profile
 * @param email - User's email for Klaviyo identification
 * @returns Promise resolving to whether permission was granted and subscription succeeded
 */
export async function requestPushPermission(
  accountId?: string,
  email?: string,
): Promise<boolean> {
  try {
    if (typeof window === "undefined") {
      console.warn("[Klaviyo] Window not available");
      return false;
    }

    if (!isNotificationSupported()) {
      console.warn("[Klaviyo] Notifications not supported");
      return false;
    }

    const publicVapidKey = getKlaviyoVapidKey();
    if (!publicVapidKey) {
      console.error("[Klaviyo] VAPID key is not configured");
      return false;
    }

    // Check current permission state SYNCHRONOUSLY (no await before requestPermission)
    const currentPermission = Notification.permission;
    console.log("[Klaviyo] Current permission state:", currentPermission);

    // If already denied/blocked, don't try to request again
    if (currentPermission === PERMISSION_STATUS.DENIED) {
      console.warn(
        "[Klaviyo] Notifications are blocked - user must enable manually",
      );
      return false;
    }

    // If already granted, subscribe directly
    if (currentPermission === PERMISSION_STATUS.GRANTED) {
      // Clean up old Firebase SWs before subscribing
      await cleanupFirebaseServiceWorkers();
      await KlaviyoService.subscribeToPush(publicVapidKey, accountId, email);
      console.log(
        "[Klaviyo] Subscription successful (permission already granted)",
      );
      return true;
    }

    // IMPORTANT: Request permission immediately in the same call stack as user gesture
    // Do NOT await anything before this call - Edge and some browsers require
    // the permission request to be in the same synchronous call stack as the click
    const permission = await new Promise<NotificationPermission>((resolve) => {
      const permissionResult = Notification.requestPermission((result) => {
        // Callback for older browsers (Edge, older Safari)
        resolve(result);
      });

      // Modern browsers return a Promise
      if (permissionResult) {
        permissionResult.then(resolve);
      }
    });

    console.log("[Klaviyo] Permission result:", permission);
    if (permission !== PERMISSION_STATUS.GRANTED) {
      console.log("[Klaviyo] Notification permission denied");
      return false;
    }

    // Clean up old Firebase SWs before subscribing
    await cleanupFirebaseServiceWorkers();
    await KlaviyoService.subscribeToPush(publicVapidKey, accountId, email);
    console.log("[Klaviyo] Subscription successful");
    return true;
  } catch (error) {
    console.error("[Klaviyo] Error requesting permission:", error);
    return false;
  }
}
